import styled from 'styled-components';

import { Collapse } from '@components/animators/collapse';
import { Checkbox } from '@components/checkbox';
import { FileField } from '@components/file-field';
import { FileAcceptText } from '@components/file-field/file-accept-text';
import { FileFieldType } from '@components/form/v2';
import { HorizontalTabItem } from '@components/horizontal-tabs';
import { IconAttributeTraveller } from '@components/icons/attribute-traveller';
import { IconSettings } from '@components/icons/settings';
import { IconUpload } from '@components/icons/upload';
import { Separator } from '@components/separator';
import { Slider } from '@components/slider';
import { Stack } from '@components/stack';
import { LabeledSwitch } from '@components/switch';
import { FieldMappingEditorValue } from '@components/text-editors/addons/field-mapping';
import { TextField } from '@components/text-field';
import { getScopedTranslate } from '@locale/translate';
import { noop } from '@utils/browser';
import { pxToRem } from '@utils/converters';
import { getMaxFileSizeMb } from '@utils/file-size';
import { getRestrictedAccept } from '@utils/file-types';
import { asteriskLabel } from '@utils/strings';

import { FieldDropzone } from './field-dropzone';
import { MaxFileSizeSetting } from './max-file-size-setting';

import { conditionsTab } from '../conditions-tab';
import { useFormBuilderContext } from '../context';
import { DefaultValueMapping, DefaultValueMappingDisplay } from '../default-value-mapping';
import { DescriptionInput } from '../description-input';
import { FieldEdit } from '../field-edit';
import { FieldConfig } from '../field-registry';
import { FieldSidebarLabel } from '../fields-sidebar';
import { RequiredSwitch } from '../required-switch';
import { StaleConditionWarningIcon } from '../stale-condition-warning';

type Config = FieldConfig<'input-file'>;

const Display: Config['Display'] = ({ field }) => {
  const mappedDefault = field.mappedDefaultValue;
  const label = asteriskLabel(field.label, field.isRequired);
  const labelIcon = field.companyAttributeId ? <IconAttributeTraveller size={16} /> : undefined;
  const accept = getRestrictedAccept(field.restrictTypes, field.accept);
  const maxFileSizeMb = getMaxFileSizeMb(field);

  // Extract each {{ variable }} occurrence from the value string.
  // The same variable can appear multiple times (e.g. same file mapped twice).
  const variableOccurrences = mappedDefault?.value?.match(/\{\{\s*([^}]+?)\s*\}\}/g) ?? [];
  const hasMultipleFiles = variableOccurrences.length > 1;

  if (hasMultipleFiles && mappedDefault?.variables) {
    return (
      <Stack space={1}>
        <Stack space={2}>
          {variableOccurrences.map((match, index) => {
            const key = match.replace(/\{\{\s*|\s*\}\}/g, '');
            const singleValue: FieldMappingEditorValue = {
              value: `{{ ${key} }}`,
              variables: { [key]: mappedDefault.variables![key] },
            };
            const singleField = {
              ...field,
              mappedDefaultValue: singleValue,
            };
            return (
              <DefaultValueMappingDisplay key={`${key}-${index}`} field={singleField}>
                <FileField
                  onChange={noop}
                  label={index === 0 ? label : undefined}
                  description={index === 0 ? field.description : undefined}
                  placeholder={field.placeholder}
                  labelIcon={index === 0 ? labelIcon : undefined}
                  labelIconAfter={
                    index === 0 ? <StaleConditionWarningIcon field={field} /> : undefined
                  }
                />
              </DefaultValueMappingDisplay>
            );
          })}
        </Stack>
        <FileAcceptText accept={accept} maxFileSizeMb={maxFileSizeMb} />
      </Stack>
    );
  }

  return (
    <Stack space={1}>
      <DefaultValueMappingDisplay field={field}>
        <FileField
          onChange={noop}
          label={label}
          description={field.description}
          placeholder={field.placeholder}
          labelIcon={labelIcon}
          labelIconAfter={<StaleConditionWarningIcon field={field} />}
        />
      </DefaultValueMappingDisplay>
      <FileAcceptText accept={accept} maxFileSizeMb={maxFileSizeMb} />
    </Stack>
  );
};

const FormBuilderFile: Config['Component'] = ({ field, dragHandle }) => {
  const actions = useFormBuilderContext().store.actions;

  return (
    <FieldDropzone field={field}>
      <FieldEdit
        dragHandle={dragHandle}
        field={field}
        display={<Display field={field} />}
        edit={
          <>
            <TextField
              onChange={actions.createChangeHandler(field, 'label')}
              defaultValue={field.label}
              label={t('label')}
              maxChars={255}
            />
            <DescriptionInput field={field} />
            <TextField
              onChange={actions.createChangeHandler(field, 'placeholder')}
              defaultValue={field.placeholder}
              label={t('placeholder')}
            />
            <RequiredSwitch field={field} />
            <DefaultValueMapping field={field} />
          </>
        }
      />
    </FieldDropzone>
  );
};

const { t } = getScopedTranslate({
  en: {
    allowMultiple: 'Allow multiple files',
    title: 'File upload',
    properties: 'Properties',
    label: 'Label',
    placeholder: 'Placeholder text',
    restrict: 'Restrict allowed file types',
    documents: 'Documents',
    images: 'Images',
    data: 'Data',
    archives: 'Archives',
    maxCount: 'Maximum files',
  },
});

const suffixGroups = ['documents', 'images', 'data', 'archives'] as const;
type SuffixGroup = (typeof suffixGroups)[number];
const suffixes: Record<SuffixGroup, Array<string>> = {
  documents: ['.pdf', '.doc', '.docx', '.rtf', '.txt', '.odt'],
  images: ['.png', '.gif', '.jpg', '.heic'],
  data: ['.csv', '.xml', '.json', '.xls', '.xlsx', '.numbers', '.ods'],
  archives: ['.zip', '.rar'],
};

const FilePropertiesTab = ({ field }: { field: FileFieldType }) => {
  const {
    store: { actions },
  } = useFormBuilderContext();

  const handleAllowMultipleChange = (value: boolean) => {
    actions.createChangeHandler(field, 'allowMultiple')(value);
    if (!value) {
      const mapped = field.mappedDefaultValue;
      const variables = mapped?.variables;
      if (variables && Object.keys(variables).length > 1) {
        const firstKey = Object.keys(variables)[0];
        actions.createChangeHandler(
          field,
          'mappedDefaultValue',
        )({
          value: `{{ ${firstKey} }}`,
          variables: { [firstKey]: variables[firstKey] },
        });
      }
    }
  };

  const accept = field.accept;
  const setAccept = actions.createChangeHandler(field, 'accept');
  const acceptList = accept.split(',').filter(Boolean); // remove empty string

  const toggleSuffix = (suffix: string) => {
    let newAccept = acceptList.filter((accepted) => accepted !== suffix);
    if (newAccept.length === acceptList.length) {
      newAccept = [...newAccept, suffix];
    }
    setAccept(newAccept.join(','));
  };

  const all: Record<SuffixGroup, boolean> = {
    documents: suffixes.documents.every((suffix) => acceptList.includes(suffix)),
    images: suffixes.images.every((suffix) => acceptList.includes(suffix)),
    data: suffixes.data.every((suffix) => acceptList.includes(suffix)),
    archives: suffixes.archives.every((suffix) => acceptList.includes(suffix)),
  };

  const some: Record<SuffixGroup, boolean> = {
    documents: suffixes.documents.some((suffix) => acceptList.includes(suffix)),
    images: suffixes.images.some((suffix) => acceptList.includes(suffix)),
    data: suffixes.data.some((suffix) => acceptList.includes(suffix)),
    archives: suffixes.archives.some((suffix) => acceptList.includes(suffix)),
  };

  const turnOnAllInGroup = (group: SuffixGroup) => {
    const groupSuffixes = suffixes[group];
    const newAcceptList = acceptList.filter((suffix) => !groupSuffixes.includes(suffix));
    setAccept(newAcceptList.concat(groupSuffixes).join(','));
  };

  const turnOffAllInGroup = (group: SuffixGroup) => {
    const groupSuffixes = suffixes[group];
    const newAcceptList = acceptList.filter((suffix) => !groupSuffixes.includes(suffix));
    setAccept(newAcceptList.join(','));
  };

  return (
    <Content>
      <LabeledSwitch
        label={t('restrict')}
        value={Boolean(field.restrictTypes)}
        onChange={actions.createChangeHandler(field, 'restrictTypes')}
      />
      <Collapse isCollapsed={!field.restrictTypes}>
        <Stack space={4}>
          {suffixGroups.map((group) => (
            <Stack space={2} key={group}>
              <Checkbox
                label={t(group).toUpperCase()}
                labelFont="semibold14"
                value={all[group] ? true : some[group] ? 'mixed' : false}
                onChange={() => (all[group] ? turnOffAllInGroup(group) : turnOnAllInGroup(group))}
              />
              <Container>
                {suffixes[group].map((suffix) => (
                  <Checkbox
                    key={suffix}
                    value={acceptList.includes(suffix)}
                    label={suffix}
                    onChange={() => toggleSuffix(suffix)}
                  />
                ))}
              </Container>
            </Stack>
          ))}
        </Stack>
      </Collapse>
      <Separator />
      {field.maximumFileCount === undefined ? (
        <LabeledSwitch
          label={t('allowMultiple')}
          value={Boolean(field.allowMultiple)}
          onChange={handleAllowMultipleChange}
          isDisabled={field.builderProps?.canEditFilesMultiple === false}
        />
      ) : (
        <Slider
          label={t('maxCount')}
          minValue={1}
          maxValue={10}
          value={field.maximumFileCount ?? 1}
          onChange={(val) => actions.createChangeHandler(field, 'maximumFileCount')(val)}
          isDisabled={field.builderProps?.canEditFilesMultiple === false}
        />
      )}
      <MaxFileSizeSetting field={field} />
    </Content>
  );
};

const propertiesTab = (field: FileFieldType): HorizontalTabItem => {
  return {
    label: t('properties'),
    icon: IconSettings,
    content: <FilePropertiesTab field={field} />,
  };
};

export const fileConfig: Config = {
  kind: 'input-file',
  title: t('title'),
  group: 'inputs',
  propertiesTabs: [propertiesTab, conditionsTab],
  Display,
  TreeDisplay: ({ field }) => <FieldSidebarLabel label={field.label} />,
  Icon: IconUpload,
  Component: FormBuilderFile,
};

const Content = styled(Stack).attrs({ space: 4 })({
  padding: pxToRem(24),
});

const Container = styled.div({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: pxToRem(4),
});
